import { AI_DAILY_LIMITS, aiLimitLabel } from './aiLimits';

export const PLANS = [
  {
    id: 'free',
    name: 'Free',
    monthly: 0,
    yearly: 0,
    highlight: false,
    cta: 'Create free account',
    ctaPath: '/login',
    checkout: false,
    tagline: 'Full learning loop with a daily AI quota.',
    upgradeNote: 'You stay on Free until you complete Pro checkout in Stripe.',
    features: [
      'Notebook hub — PDF import, catalog subscribe & server-side search',
      'Save, practice & spaced repetition',
      'Learning dashboard & analytics',
      `AI explain in depth (${AI_DAILY_LIMITS.free} requests / day)`,
      'Optional two-factor authentication',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthly: 12,
    yearly: 120,
    highlight: true,
    cta: 'Upgrade with checkout',
    ctaPath: '/checkout',
    checkout: true,
    planKey: 'pro',
    tagline: 'Higher AI limits and power-user analytics.',
    upgradeNote:
      `After checkout you are charged via Stripe. AI limit rises to ${AI_DAILY_LIMITS.pro}/day immediately; billing renews monthly or yearly.`,
    features: [
      'Everything in Free',
      'Share notebooks with your team (Pro)',
      'Invite collaborators by email (Pro)',
      'Mind map AI search & related-topic discovery',
      `${AI_DAILY_LIMITS.pro} AI requests / day`,
      'Advanced analytics & JSON export',
      'Stripe-managed subscription & portal',
    ],
  },
  {
    id: 'team',
    name: 'Team',
    monthly: 29,
    yearly: 290,
    highlight: false,
    cta: 'Open Teams workspace',
    ctaPath: '/teams',
    checkout: false,
    tagline: 'Collaborative workspaces for groups and companies.',
    upgradeNote:
      'Create a team at Teams → Create team. Seat limits and Stripe billing roll out next; trial workspaces are enabled today.',
    features: [
      'Everything in Pro (per seat)',
      'Team & email notebook sharing for Learn',
      `${AI_DAILY_LIMITS.team} AI requests / day (team quota model)`,
      'Team workspace — members, invites, roles',
      'Assignments, challenges & team leaderboard',
      'Team learning score & manager dashboard',
      'Groups, audit log & leaderboard privacy settings',
      'Centralized billing (Stripe — coming soon)',
    ],
  },
];

/** Row-wise comparison for upgrade UI (Free / Pro / Team). */
export const PLAN_FEATURE_ROWS = [
  { label: 'Notebook hub & PDF import (OCR for scans)', free: true, pro: true, team: true },
  { label: 'Public catalog — browse & subscribe', free: true, pro: true, team: true },
  { label: 'Notebook search & filters', free: true, pro: true, team: true },
  { label: 'Save questions & practice decks', free: true, pro: true, team: true },
  { label: 'Spaced repetition review', free: true, pro: true, team: true },
  { label: 'Learning dashboard & streaks', free: true, pro: true, team: true },
  { label: 'Analytics & JSON export', free: 'Basic', pro: 'Advanced', team: 'Advanced + team' },
  {
    label: 'AI explain in depth',
    free: aiLimitLabel('free'),
    pro: aiLimitLabel('pro'),
    team: aiLimitLabel('team'),
  },
  { label: 'Rank & leaderboard (XP rankings)', free: false, pro: true, team: true },
  { label: 'Mind map AI search', free: false, pro: true, team: true },
  { label: 'Stripe checkout & billing portal', free: false, pro: true, team: 'Centralized' },
  { label: 'Two-factor authentication', free: true, pro: true, team: true },
  { label: 'Private learn notebooks', free: true, pro: true, team: true },
  { label: 'Share notebook with team', free: false, pro: true, team: true },
  { label: 'Invite notebook collaborator (email)', free: false, pro: true, team: true },
  { label: 'Team workspace & invites', free: false, pro: false, team: true },
  { label: 'Assignments & challenges', free: false, pro: false, team: true },
  { label: 'Team leaderboard & analytics', free: false, pro: false, team: true },
  { label: 'Team AI & SSO', free: false, pro: false, team: 'Next' },
];

export const PLAN_FAQ = [
  {
    q: 'Is the Free plan fully usable?',
    a: 'Yes — search, save, practice, review, dashboard, analytics, MFA, and a daily AI quota are included on Free.',
  },
  {
    q: 'What happens when I click Upgrade?',
    a: 'Pro opens Stripe Checkout (or demo upgrade if billing demo mode is on). Your plan and AI limit update after successful payment. Team workspaces are available at Teams — create an organization and invite members (trial seats enabled; Stripe seat billing rolls out next).',
  },
  {
    q: 'How does checkout work?',
    a: 'Pro upgrades use Stripe Checkout (hosted, PCI-compliant). Configure stripe.secret-key on the API, or enable qnaread.billing.demo-mode for local testing.',
  },
  {
    q: 'What counts as an AI request?',
    a: 'Each AI explain, mind map, or related-topic search counts toward your per-plan daily limit (shown on Study hub and in Settings). Caps are tuned to stay within the shared Gemini provider quota; raise limits on the API when your Google AI project allows. Mind map AI search requires Pro or Team.',
  },
  {
    q: 'Can I use QnaRead on mobile?',
    a: 'Yes — the app is responsive and installable as a PWA.',
  },
];

export function planCellValue(value) {
  if (value === true) return '✓';
  if (value === false) return '—';
  return String(value);
}
